import {json, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData, useMatches} from '@remix-run/react';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {HELMETKING_LOGO_URL} from '~/lib/const';
import Container from 'components/Container';
import type {I18nLocale} from '~/lib/type';
import {canonicalOrigin, canonicalUrl} from '~/lib/seo.server';

export const headers = routeHeaders;

/**
 * Native store-locator page (replaces the Mapify theme app, which cannot run
 * on the headless storefront). Locations are code-owned — the shop has a
 * single retail flagship today; add more entries to STORES as they open.
 */

interface StoreInfo {
  name: {zh: string; en: string};
  address: {zh: string; en: string};
  /** exact query for Google Maps (address + place name in Chinese). */
  mapQuery: string;
  /** Google Business Profile place ID, so directions land on the listing. */
  placeId: string;
  /** The listing's pin, copied from Google Maps (5+ decimals). */
  geo: {latitude: number; longitude: number};
  phone: string;
  hours: {zh: string; en: string};
}

const STORES: StoreInfo[] = [
  {
    name: {zh: '頭盔王 旺角旗艦店', en: 'Helmetking Mongkok Flagship'},
    address: {
      zh: '九龍旺角登打士街2-8號寶亨大廈地下1號舖',
      en: 'Shop 1, G/F, Po Hang Building, 2-8 Dundas Street, Mong Kok, Kowloon',
    },
    mapQuery: '頭盔王 旺角登打士街2-8號寶亨大廈地下1號舖',
    placeId: 'ChIJ59-sCrUABDQRPYvwB8UYoRY',
    geo: {latitude: 22.3145641, longitude: 114.1675865},
    phone: '+852 6385 8830',
    hours: {
      zh: '星期一至日 11:00–20:30（公眾假期照常）',
      en: 'Mon–Sun 11:00–20:30 (open on public holidays)',
    },
  },
];

export async function loader({request, context}: LoaderArgs) {
  const origin = canonicalOrigin(request.url);
  const flagship = STORES[0];
  const isEn = context.storefront.i18n.language === 'EN';
  const listingUrl = `https://www.google.com/maps/place/?q=place_id:${flagship.placeId}`;
  const seo = {
    // The root template appends the site name, so it is not repeated here.
    title: isEn
      ? 'Store Locator – Mong Kok Flagship'
      : '門市地址及營業時間 | 旺角旗艦店',
    description: isEn
      ? `Helmetking's Mong Kok flagship: ${flagship.address.en}. Open ${flagship.hours.en}. Tel ${flagship.phone}.`
      : `頭盔王旺角旗艦店地址、電話及營業時間 — ${flagship.address.zh}，${flagship.hours.zh}。`,
    // Tracking params (utm_*, fbclid, gclid) would otherwise land in this
    // page's own canonical, so every tagged share minted a new indexable
    // URL. canonicalUrl keeps only the params on its allowlist.
    url: canonicalUrl(request.url),
    // Store schema so local queries ("motorcycle helmet shop Hong Kong")
    // resolve to the flagship. Built from the same STORES const the page
    // renders, so the schema can never drift from the visible data.
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Store',
      '@id': `${origin}/store-locator#mongkok`,
      name: isEn ? flagship.name.en : flagship.name.zh,
      // The second name is the Google Business Profile listing's own name.
      alternateName: [flagship.name.en, '頭盔王電單車 Helmet King'],
      url: canonicalUrl(request.url),
      image: HELMETKING_LOGO_URL,
      telephone: flagship.phone,
      currenciesAccepted: 'HKD',
      geo: {'@type': 'GeoCoordinates', ...flagship.geo},
      sameAs: [listingUrl],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Shop 1, G/F, Po Hang Building, 2-8 Dundas Street',
        addressLocality: 'Mong Kok',
        addressRegion: 'Kowloon',
        addressCountry: 'HK',
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '11:00',
        closes: '20:30',
      },
      hasMap: listingUrl,
      parentOrganization: {'@id': `${origin}/#organization`},
    },
  };

  return json({seo, stores: STORES}, {headers: {'Cache-Control': CACHE_LONG}});
}

export default function StoreLocator() {
  const {stores} = useLoaderData<typeof loader>();
  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale as I18nLocale | undefined;
  const isEn = selectedLocale?.language === 'EN';

  const t = {
    heading: isEn ? 'Store Locator' : '門市地址',
    address: isEn ? 'Address' : '地址',
    phone: isEn ? 'Phone' : '電話',
    hours: isEn ? 'Opening hours' : '營業時間',
    directions: isEn ? 'Get directions' : '導航前往',
    call: isEn ? 'Call' : '致電',
  };

  return (
    <section className="lg:pt-10 relative">
      <Container>
        <header className="my-8 text-center lg:my-10">
          <h1 className="text-xl font-bold uppercase tracking-wide lg:text-[28px] lg:leading-[40px]">
            {t.heading}
          </h1>
          <span className="mt-3 inline-block h-[3px] w-12 rounded-full bg-hk-orange" />
        </header>

        <div className="mx-auto max-w-5xl space-y-14 pb-20">
          {stores.map((store) => {
            const q = encodeURIComponent(store.mapQuery);
            const embedSrc = `https://www.google.com/maps?q=${q}&hl=${
              isEn ? 'en' : 'zh-TW'
            }&z=17&output=embed`;
            const directionsHref = `https://www.google.com/maps/dir/?api=1&destination=${q}&destination_place_id=${store.placeId}`;
            const telHref = `tel:${store.phone.replace(/[^\d+]/g, '')}`;
            return (
              <div
                key={store.mapQuery}
                className="grid gap-6 lg:grid-cols-2 lg:gap-10"
              >
                <div className="order-2 lg:order-1">
                  <h2 className="text-2xl font-bold">
                    {isEn ? store.name.en : store.name.zh}
                  </h2>
                  <dl className="mt-5 space-y-4 text-[15px] leading-relaxed">
                    <div>
                      <dt className="font-semibold text-hk-orange">
                        {t.address}
                      </dt>
                      <dd className="mt-1">
                        {isEn ? store.address.en : store.address.zh}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-hk-orange">
                        {t.phone}
                      </dt>
                      <dd className="mt-1">
                        <a className="hover:underline" href={telHref}>
                          {store.phone}
                        </a>
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-hk-orange">
                        {t.hours}
                      </dt>
                      <dd className="mt-1">
                        {isEn ? store.hours.en : store.hours.zh}
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={directionsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-sm bg-hk-orange px-5 py-2 text-sm font-semibold text-black transition-opacity hover:opacity-90"
                    >
                      {t.directions}
                    </a>
                    <a
                      href={telHref}
                      className="rounded-sm border border-black px-5 py-2 text-sm font-semibold transition-colors hover:bg-black hover:text-white"
                    >
                      {t.call}
                    </a>
                  </div>
                </div>

                <div className="order-1 overflow-hidden rounded-sm border border-gray-200 lg:order-2">
                  <iframe
                    title={isEn ? store.name.en : store.name.zh}
                    src={embedSrc}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="h-[280px] w-full lg:h-[340px]"
                    style={{border: 0}}
                    allowFullScreen
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
